// Theme toggle — binary light ⇄ dark. The player's explicit choice
// persists in the settings store; before any choice is made the setting is
// 'system', which resolves through `prefers-color-scheme` (see theme.ts and
// the inline pre-paint resolver in index.html). The trigger shows the icon
// of the theme it will SWITCH TO (sun in dark mode, moon in light mode).

import { Button } from '@aod/ui/components/ui/button';
import { Moon, Sun } from 'lucide-react';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { useSettings } from '../../settings/useSettings.js';
import { type ResolvedTheme, resolveTheme, systemPrefersDark } from '../../theme/theme.js';

export function ThemeToggle() {
  const t = useT();
  const theme = useSettings((s) => s.theme);
  const setTheme = useSettings((s) => s.setTheme);
  const resolved: ResolvedTheme = resolveTheme(theme, systemPrefersDark());
  // Click switches to the opposite of what's showing.
  const next: ResolvedTheme = resolved === 'dark' ? 'light' : 'dark';
  const NextIcon = next === 'dark' ? Moon : Sun;
  const label = t('theme.switch', { theme: t(`theme.${next}`) });
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      data-testid="theme-toggle"
      {...appDom({
        id: 'theme-toggle',
        type: 'Button',
        role: 'button',
        scope: 'header',
        label: 'Theme',
      })}
      data-theme-preference={theme}
      data-theme-resolved={resolved}
      aria-label={label}
      title={label}
      className="size-8"
      onClick={() => setTheme(next)}
    >
      <NextIcon className="size-4" />
    </Button>
  );
}
